import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { IoPulseOutline, IoChevronDown, IoEyeOutline, IoKeyOutline, IoLogOutOutline, IoPeopleOutline, IoSettingsOutline } from "react-icons/io5";
import { useAuth } from "../../contexts/AuthContext";
import ChangePasswordDialog from "../auth/ChangePasswordDialog";
import { useOrg } from "../../hooks/useOrg";
import { useStaffOrganisations } from "../../hooks/useReadOnly";
import { getSupportView, stopSupportView } from "../../services/supportView";

const ChevronIcon = IoChevronDown as any;
const KeyIcon = IoKeyOutline as any;
const LogoutIcon = IoLogOutOutline as any;
const PeopleIcon = IoPeopleOutline as any;
const SettingsIcon = IoSettingsOutline as any;
const EyeIcon = IoEyeOutline as any;
const StatusIcon = IoPulseOutline as any;

// The signed-in user's menu in the top bar: who you are and in which organisation,
// your own account (change password, log out) and, for editors, the
// organisation's team (Trello #562).
const AccountMenu: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { organisation, role, isEditor } = useOrg();
  const { isStaff } = useStaffOrganisations();
  const supportView = getSupportView();
  const [open, setOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!currentUser) return null;
  const email = currentUser.email ?? "";
  const initial = (currentUser.displayName || email || "?").charAt(0).toUpperCase();
  const item =
    "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-paper-100";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-full border border-paper-300 py-1 pl-1 pr-2.5 text-sm text-ink-muted transition-colors duration-200 hover:bg-paper-200 hover:text-ink"
      >
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-500 text-xs font-semibold text-on-brand">
          {initial}
        </span>
        <span className="hidden max-w-[200px] truncate lg:inline">{email}</span>
        <ChevronIcon className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-xl border border-paper-300/70 bg-surface py-1 shadow-card"
        >
          <div className="border-b border-paper-200 px-3 py-2.5">
            <p className="truncate text-sm font-medium text-ink">{email}</p>
            {organisation && (
              <p className="truncate text-xs text-ink-faint">
                {organisation.name ?? "Your organisation"} ·{" "}
                {supportView ? "Support view, read-only" : role === "editor" ? "Editor" : "Viewer"}
              </p>
            )}
          </div>
          <button
            role="menuitem"
            className={item}
            onClick={() => {
              setOpen(false);
              setChangingPassword(true);
            }}
          >
            <KeyIcon className="h-4 w-4 text-ink-muted" />
            Change password
          </button>
          <Link role="menuitem" to="/settings/account" className={item} onClick={() => setOpen(false)}>
            <SettingsIcon className="h-4 w-4 text-ink-muted" />
            Settings
          </Link>
          <Link role="menuitem" to="/?section=status" className={item} onClick={() => setOpen(false)}>
            <StatusIcon className="h-4 w-4 text-ink-muted" />
            Status
          </Link>
          {isEditor && (
            <Link role="menuitem" to="/settings/team" className={item} onClick={() => setOpen(false)}>
              <PeopleIcon className="h-4 w-4 text-ink-muted" />
              Team &amp; users
            </Link>
          )}
          {isStaff && (
            <>
              <div className="my-1 border-t border-paper-200" />
              {supportView ? (
                <button role="menuitem" className={item} onClick={stopSupportView}>
                  <EyeIcon className="h-4 w-4 text-ink-muted" />
                  Exit support view
                </button>
              ) : (
                <Link role="menuitem" to="/support" className={item} onClick={() => setOpen(false)}>
                  <EyeIcon className="h-4 w-4 text-ink-muted" />
                  View a customer (support)
                </Link>
              )}
            </>
          )}
          <div className="my-1 border-t border-paper-200" />
          <button role="menuitem" className={item} onClick={logout}>
            <LogoutIcon className="h-4 w-4 text-ink-muted" />
            Log out
          </button>
        </div>
      )}
      <ChangePasswordDialog open={changingPassword} onClose={() => setChangingPassword(false)} />
    </div>
  );
};

export default AccountMenu;
